"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TurnkeyRequestError = exports.sealAndStampRequestBody = exports.request = exports.signedRequest = exports.fetch = void 0;
const api_key_stamper_1 = require("@turnkey/api-key-stamper");
const universal_1 = require("./universal");
Object.defineProperty(exports, "fetch", { enumerable: true, get: function () { return universal_1.fetch; } });
const config_1 = require("./config");
const encoding_1 = require("./encoding");
const webauthn_1 = require("./webauthn");
const sharedHeaders = {};
const sharedRequestOptions = {
    redirect: "follow",
};
/**
 * @deprecated
 */
async function signedRequest(input) {
    const { uri: inputUri, query: inputQuery = {}, substitution: inputSubstitution = {}, body: inputBody = {}, } = input;
    const url = constructUrl({
        uri: inputUri,
        query: inputQuery,
        substitution: inputSubstitution,
    });
    const body = JSON.stringify(inputBody);
    const stamp = await (0, webauthn_1.getWebAuthnAssertion)(body, input.options);
    return {
        url: url.toString(),
        body,
        stamp,
    };
}
exports.signedRequest = signedRequest;
async function request(input) {
    const { uri: inputUri, method, headers: inputHeaders = {}, query: inputQuery = {}, substitution: inputSubstitution = {}, body: inputBody = {}, } = input;
    const url = constructUrl({
        uri: inputUri,
        query: inputQuery,
        substitution: inputSubstitution,
    });
    const { sealedBody, xStamp } = await sealAndStampRequestBody({
        body: inputBody,
    });
    const response = await (0, universal_1.fetch)(url.toString(), {
        ...sharedRequestOptions,
        method,
        headers: {
            ...sharedHeaders,
            ...inputHeaders,
            "X-Stamp": xStamp,
        },
        body: sealedBody,
    });
    if (!response.ok) {
        // Can't use native `cause` here because it's not well supported on Node v16
        // https://node.green/#ES2022-features-Error-cause-property
        let res;
        try {
            res = await response.json();
        }
        catch (_) {
            throw new Error(`${response.status} ${response.statusText}`);
        }
        throw new TurnkeyRequestError(res);
    }
    const data = await response.json();
    return data;
}
exports.request = request;
function constructUrl(input) {
    const { uri, query, substitution } = input;
    const baseUrl = getBaseUrl();
    const url = new URL(substitutePath(uri, substitution), baseUrl);
    for (const key in query) {
        const value = query[key];
        if (Array.isArray(value)) {
            for (const item of value) {
                url.searchParams.append(key, item);
            }
        }
        else {
            url.searchParams.append(key, value ?? "");
        }
    }
    return url;
}
function getBaseUrl() {
    try {
        const { baseUrl } = (0, config_1.getConfig)();
        return baseUrl;
    }
    catch (e) {
        const { baseUrl } = (0, config_1.getBrowserConfig)();
        return baseUrl;
    }
}
function substitutePath(uri, substitutionMap) {
    let result = uri;
    const keyList = Object.keys(substitutionMap);
    for (const key of keyList) {
        const output = result.replaceAll(`{${key}}`, substitutionMap[key]);
        invariant(output !== result, `Substitution error: cannot find "${key}" in URI "${uri}". \`substitutionMap\`: ${JSON.stringify(substitutionMap)}`);
        result = output;
    }
    invariant(!/\{.*\}/.test(result), `Substitution error: found unsubstituted components in "${result}"`);
    return result;
}
function invariant(condition, message) {
    if (!condition) {
        throw new Error(message);
    }
}
function stableStringify(input) {
    return JSON.stringify(input);
}
/**
 * Seals and stamps the request body with your Turnkey API credentials.
 *
 * You can either:
 * - Before calling `sealAndStampRequestBody(...)`, initialize with your Turnkey API credentials via `init(...)`
 * - Or, provide `apiPublicKey` and `apiPrivateKey` here as arguments
 */
async function sealAndStampRequestBody(input) {
    const { body } = input;
    let { apiPublicKey, apiPrivateKey } = input;
    if (!apiPublicKey) {
        const config = (0, config_1.getConfig)();
        apiPublicKey = config.apiPublicKey;
    }
    if (!apiPrivateKey) {
        const config = (0, config_1.getConfig)();
        apiPrivateKey = config.apiPrivateKey;
    }
    const sealedBody = stableStringify(body);
    const signature = await (0, api_key_stamper_1.signWithApiKey)({
        content: sealedBody,
        privateKey: apiPrivateKey,
        publicKey: apiPublicKey,
    });
    const sealedStamp = stableStringify({
        publicKey: apiPublicKey,
        scheme: "SIGNATURE_SCHEME_TK_API_P256",
        signature: signature,
    });
    const xStamp = (0, encoding_1.stringToBase64urlString)(sealedStamp);
    return {
        sealedBody,
        xStamp,
    };
}
exports.sealAndStampRequestBody = sealAndStampRequestBody;
class TurnkeyRequestError extends Error {
    constructor(input) {
        let turnkeyErrorMessage = `Turnkey error ${input.code}: ${input.message}`;
        if (input.details != null) {
            turnkeyErrorMessage += ` (Details: ${JSON.stringify(input.details)})`;
        }
        super(turnkeyErrorMessage);
        this.name = "TurnkeyRequestError";
        this.details = input.details ?? null;
        this.code = input.code;
    }
}
exports.TurnkeyRequestError = TurnkeyRequestError;
//# sourceMappingURL=base.js.map